import { useEffect, useState } from "react";
import { t } from "i18next";
import surveyApi from "../../api/survey/survey";
import { ui } from "../../helpers/ui";
import {
  SURVEY_GENERAL_ITEMS,
  SURVEY_TRAINERS,
  SurveyItem,
} from "../../config/SurveyItems";

export interface SurveyAnswer {
  score: number | null;
  comment: string;
}

const emptyAnswer = (): SurveyAnswer => ({ score: null, comment: "" });

const buildInitialAnswers = () => {
  const answers: Record<string, SurveyAnswer> = {};
  for (const item of [...SURVEY_GENERAL_ITEMS, ...SURVEY_TRAINERS]) {
    answers[item.itemKey] = emptyAnswer();
  }
  return answers;
};

const InitSurveyLogic = () => {
  // "pending" dok se ne zna status - bez toga modal bljesne i onima koji su vec popunili
  const [status, setStatus] = useState<"pending" | "todo" | "done">("pending");
  const [showModal, setShowModal] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [anonymous, setAnonymous] = useState(false);
  const [suggestions, setSuggestions] = useState("");
  const [answers, setAnswers] =
    useState<Record<string, SurveyAnswer>>(buildInitialAnswers);

  useEffect(() => {
    surveyApi
      .getStatus()
      .then((response) => {
        if (response.data?.submitted) {
          setStatus("done");
        } else {
          // Upitnik se vise ne otvara sam - stoji samo baner na vrhu
          // pocetne, a modal se otvara tek na klik na "Popuni"
          setStatus("todo");
        }
      })
      .catch(() => {
        // Upitnik ne sme da obori pocetni ekran ako status ne prodje
        setStatus("done");
      });
  }, []);

  const setScore = (itemKey: string, score: number) => {
    setAnswers((prev) => ({
      ...prev,
      // Ponovni klik na istu ocenu je ponistava
      [itemKey]: {
        ...prev[itemKey],
        score: prev[itemKey]?.score === score ? null : score,
      },
    }));
  };

  const setComment = (itemKey: string, comment: string) => {
    setAnswers((prev) => ({
      ...prev,
      [itemKey]: { ...prev[itemKey], comment },
    }));
  };

  const openModal = () => setShowModal(true);
  const closeModal = () => setShowModal(false);

  const collectRatings = () => {
    const items: SurveyItem[] = [...SURVEY_GENERAL_ITEMS, ...SURVEY_TRAINERS];
    return items
      .map((item) => ({
        itemKey: item.itemKey,
        label: item.label,
        score: answers[item.itemKey]?.score ?? null,
        comment: answers[item.itemKey]?.comment?.trim() || undefined,
      }))
      .filter((r) => r.score !== null || r.comment);
  };

  const onSubmit = async () => {
    const ratings = collectRatings();

    if (ratings.length === 0 && !suggestions.trim()) {
      ui.alert(t("Molimo Vas da ocenite bar jednu stavku."));
      return;
    }

    const confirmed = await ui.confirm(
      t("Da li ste sigurni da želite da pošaljete upitnik?"),
    );
    if (!confirmed) return;

    setSubmitting(true);
    surveyApi
      .submit({
        anonymous,
        suggestions: suggestions.trim() || undefined,
        ratings,
      })
      .then(() => {
        setStatus("done");
        setShowModal(false);
        ui.toast(t("Hvala Vam na izdvojenom vremenu!"), "success");
      })
      .catch((e) => {
        ui.alert(t(e.response?.data?.error?.message || "Greška"));
      })
      .finally(() => setSubmitting(false));
  };

  return {
    status,
    showModal,
    submitting,
    anonymous,
    setAnonymous,
    suggestions,
    setSuggestions,
    answers,
    setScore,
    setComment,
    openModal,
    closeModal,
    onSubmit,
  };
};

export default InitSurveyLogic;
